// ── Spec Health Panel (Layer 3c) ──
// Read-only telemetry for the bound JobSpec version. Shows ONLY metrics
// supported by real, durable data. Phase timing has an honest empty state.

import { Activity, Camera, CheckSquare, Clock, ShieldCheck, Sparkles } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAppData } from "@/data/AppDataContext";
import { useInstallerExecution } from "@/data/InstallerExecutionContext";
import { PROOF_POLICY_LABEL } from "@/data/installerProof";
import {
  getSpecVersionTelemetry,
  getSpecRecommendations,
  type SpecRecommendation,
} from "@/data/jobSpecTelemetry";
import type { JobSpec } from "@/data/jobSpec";

interface SpecHealthPanelProps {
  spec: JobSpec;
}

function formatPhotoLabel(key: string): string {
  return key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function pct(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

function recBadge(severity: SpecRecommendation["severity"]) {
  const cls =
    severity === "attention"
      ? "border-amber-500/40 text-amber-600 dark:text-amber-400"
      : severity === "advisory"
        ? "border-primary/40 text-primary"
        : "border-muted-foreground/30 text-muted-foreground";
  return (
    <Badge variant="outline" className={`text-[9px] uppercase tracking-wider ${cls}`}>
      {severity}
    </Badge>
  );
}

function EmptyTile({ icon: Icon, label, message }: {
  icon: typeof Camera;
  label: string;
  message: string;
}) {
  return (
    <div className="rounded-md border border-dashed bg-muted/10 p-3 flex items-start gap-2">
      <Icon className="h-3.5 w-3.5 text-muted-foreground mt-0.5" />
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <p className="text-xs text-muted-foreground mt-1">{message}</p>
      </div>
    </div>
  );
}

export function SpecHealthPanel({ spec }: SpecHealthPanelProps) {
  const { jobs, specExecutions } = useAppData();
  const { proofGapEvents } = useInstallerExecution();

  const t = getSpecVersionTelemetry({
    spec,
    jobs,
    specExecutions,
    proofGapEvents,
  });
  const recs = getSpecRecommendations(t);

  // Hard empty state — no bound jobs at all.
  if (t.empty.noBoundJobs) {
    return (
      <Card className="shadow-none border-dashed">
        <CardContent className="p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Not Enough Data Yet
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            No jobs are currently bound to v{spec.versionNumber} of this spec.
            Telemetry will appear here once published versions reach the field.
          </p>
        </CardContent>
      </Card>
    );
  }

  const recCount = recs.length;

  return (
    <div className="space-y-4">
      {/* Recommendation strip — advisory only */}
      <div className="rounded-md border bg-muted/10 p-3 space-y-2">
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Recommendations
          </p>
          <Badge variant="outline" className="text-[9px] uppercase tracking-wider">
            advisory
          </Badge>
        </div>
        {recCount === 0 ? (
          <p className="text-xs text-muted-foreground">
            No advisories. Required items are within expected gap thresholds for the
            observed sample.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {recs.map((r) => (
              <li key={r.id} className="flex items-start gap-2">
                {recBadge(r.severity)}
                <span className="text-xs text-foreground/80 flex-1">{r.message}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Volume strip */}
      <div className="grid gap-3 grid-cols-3">
        <div className="rounded-md border bg-muted/20 p-3">
          <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
            Bound Jobs
          </p>
          <p className="text-lg font-semibold tabular-nums mt-1">{t.boundJobCount}</p>
          <p className="text-[10px] text-muted-foreground">
            on v{t.versionNumber}
          </p>
        </div>
        <div className="rounded-md border bg-muted/20 p-3">
          <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
            Phase Submits
          </p>
          <p className="text-lg font-semibold tabular-nums mt-1">{t.totalPhaseSubmits}</p>
          <p className="text-[10px] text-muted-foreground">observed</p>
        </div>
        <div className="rounded-md border bg-muted/20 p-3">
          <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
            Proof-Gap Events
          </p>
          <p className="text-lg font-semibold tabular-nums mt-1">
            {t.empty.noProofGapEvents
              ? "—"
              : t.photoGaps.reduce((s, g) => s + g.missCount, 0) +
                t.checklistSkips.reduce((s, c) => s + c.skipCount, 0)}
          </p>
          <p className="text-[10px] text-muted-foreground">
            {t.empty.noProofGapEvents ? "none recorded" : "missing items logged"}
          </p>
        </div>
      </div>

      {/* Photo gaps */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <Camera className="h-3.5 w-3.5 text-muted-foreground" />
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Most-Missed Required Photos
          </p>
        </div>
        {t.photoGaps.length === 0 ? (
          <EmptyTile
            icon={Camera}
            label="No Gap Events"
            message="No required-photo gap events recorded for this spec version."
          />
        ) : (
          <div className="rounded-md border overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-left font-medium px-3 py-1.5">Photo</th>
                  <th className="text-left font-medium px-3 py-1.5 w-20">Phase</th>
                  <th className="text-right font-medium px-3 py-1.5 w-20">Missed</th>
                  <th className="text-right font-medium px-3 py-1.5 w-20">Rate</th>
                </tr>
              </thead>
              <tbody>
                {t.photoGaps.map((g) => (
                  <tr key={`${g.phase}-${g.photoType}`} className="border-t">
                    <td className="px-3 py-1.5">{formatPhotoLabel(g.photoType)}</td>
                    <td className="px-3 py-1.5 text-muted-foreground capitalize">
                      {g.phase}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {g.missCount} / {g.observedSubmits}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-semibold">
                      {pct(g.rate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Checklist skips */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <CheckSquare className="h-3.5 w-3.5 text-muted-foreground" />
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Most-Skipped Required Checklist Items
          </p>
        </div>
        {t.checklistSkips.length === 0 ? (
          <EmptyTile
            icon={CheckSquare}
            label="No Skip Events"
            message="No required-checklist skip events recorded for this spec version."
          />
        ) : (
          <div className="rounded-md border overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-left font-medium px-3 py-1.5">Checklist Item</th>
                  <th className="text-left font-medium px-3 py-1.5 w-20">Phase</th>
                  <th className="text-right font-medium px-3 py-1.5 w-20">Skipped</th>
                  <th className="text-right font-medium px-3 py-1.5 w-20">Rate</th>
                </tr>
              </thead>
              <tbody>
                {t.checklistSkips.map((c) => (
                  <tr key={`${c.phase}-${c.itemId}`} className="border-t">
                    <td className="px-3 py-1.5">{c.itemLabel}</td>
                    <td className="px-3 py-1.5 text-muted-foreground capitalize">
                      {c.phase}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {c.skipCount} / {c.observedSubmits}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-semibold">
                      {pct(c.rate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Policy distribution */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Proof Policy at Submit
          </p>
        </div>
        {t.policyDistribution.length === 0 ? (
          <EmptyTile
            icon={ShieldCheck}
            label="No Policy Data"
            message="No proof-gap events have been recorded yet, so policy distribution can't be derived."
          />
        ) : (
          <div className="flex flex-wrap gap-2">
            {t.policyDistribution.map((p) => (
              <div
                key={p.policy}
                className="rounded-md border bg-muted/20 px-3 py-1.5 flex items-center gap-2"
              >
                <span className="text-xs font-medium">
                  {PROOF_POLICY_LABEL[p.policy]}
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {p.count}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Phase timing — honest empty state */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Time in Phase
          </p>
        </div>
        <EmptyTile
          icon={Clock}
          label="Not Captured"
          message="Phase timing not currently captured. Telemetry will appear here once phase enter/exit timestamps are recorded by the installer flow."
        />
      </div>

      <p className="text-[10px] text-muted-foreground flex items-center gap-1.5">
        <Activity className="h-3 w-3" />
        Derived live from execution + proof-gap data attributed to v{t.versionNumber}.
        No persistence, no proxies.
      </p>
    </div>
  );
}

export default SpecHealthPanel;
